import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FolderOpen, Ticket, AlertTriangle } from 'lucide-react';
import ProjectTypeBadge from './ProjectTypeBadge';
import HoursProgressBar from './HoursProgressBar';
import DeadlineBadge from './DeadlineBadge';
import { getProjectStatusLabel } from '../types';
import type { ProjectType, ProjectStatus } from '../types';

interface ProjectCardProps {
  id: string;
  name: string;
  type: ProjectType;
  status: ProjectStatus;
  ticketCount: number;
  openTicketCount: number;
  totalActualHours: number;
  totalBudgetHours?: number;
  organizationName?: string;
  overdueTicketCount?: number;
  endDate?: string;
}

const statusDot: Record<ProjectStatus, string> = {
  active: 'bg-green-400',
  on_hold: 'bg-yellow-400',
  completed: 'bg-blue-400',
  cancelled: 'bg-surface-400',
};

export default function ProjectCard({
  id, name, type, status, ticketCount, openTicketCount,
  totalActualHours, totalBudgetHours, organizationName,
  overdueTicketCount, endDate,
}: ProjectCardProps) {
  const { t } = useTranslation();
  return (
    <Link to={`/projects/${id}`}
      className="card hover:shadow-card transition-shadow border border-surface-400/15 block">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <FolderOpen size={20} className="text-primary-500 flex-shrink-0" />
          <h3 className="font-semibold text-surface-800 truncate">{name}</h3>
        </div>
        <ProjectTypeBadge type={type} />
      </div>

      <div className="flex items-center gap-2 mb-3">
        <span className={`w-2 h-2 rounded-full ${statusDot[status]}`} />
        <span className="text-xs text-surface-500">{getProjectStatusLabel(status, t)}</span>
        {organizationName && (
          <span className="text-xs text-surface-400">· {organizationName}</span>
        )}
      </div>

      <div className="flex items-center gap-4 text-xs text-surface-500 mb-3 flex-wrap">
        <span className="flex items-center gap-1">
          <Ticket size={12} />
          {t('project.openOfTotal', { open: openTicketCount, total: ticketCount })}
        </span>
        {overdueTicketCount != null && overdueTicketCount > 0 && (
          <span className="flex items-center gap-1 text-danger-600">
            <AlertTriangle size={12} />
            {t('project.overdue', { count: overdueTicketCount })}
          </span>
        )}
        {endDate && <DeadlineBadge dueDate={endDate} source="project" />}
      </div>

      {totalBudgetHours && totalBudgetHours > 0 ? (
        <HoursProgressBar actual={totalActualHours} estimated={totalBudgetHours} />
      ) : (
        <div className="text-xs text-surface-400">{t('project.actualHoursLabel', { hours: totalActualHours.toFixed(1) })}</div>
      )}
    </Link>
  );
}
